// The Blueprint day board in ui-web's round 2 components (ROUND-2.md): what the board's reads say, in
// the shapes BoardLane, VisitBlock, BoardBand, StatusBar, JobTable and TechMenu draw. Pure: Board.tsx
// holds the state and passes these the minutes and words it needs.
import type {
  BandFigure,
  JobColumn,
  JobRow,
  KeyValueItem,
  LaneLine,
  LaneOff,
  LaneVisit,
  Pen,
  StatusTab,
  TechDay,
  TechMenuRole,
  TechNow,
  TechTruck,
  TimeProblem,
  VisitState,
} from '@flowology/ui-web/fl';
import { gapsText } from './settings/licences-model.ts';
import { scopeLead } from '@flowology/domain';
import { localDateOf } from '@flowology/kernel';
import { plainText } from './shared/plain-text.ts';
import {
  clock,
  DAY_START_HOUR,
  laneBands,
  laneStatus,
  shownSpan,
  shownStatus,
  workDone,
  lookWhy,
  overdueTone,
  overdueWords,
  pausedGroups,
  pausedSince,
  pauseWords,
  tagsLabel,
  techMessagesWords,
  visitStatusLabel,
  windowText,
  type BoardTech,
  type BoardVisit,
  type Overdue,
} from './board-model.ts';
import { minuteOf } from './board-drag-model.ts';
import { boardTitle, laneSummary, penOf } from './blueprint/schedule-model.ts';
import type { ListTab } from './board-extras-model.ts';
import { VISIT_WORDS } from '@flowology/ui-web/words';
import { LIST_TABS } from './board-extras-model.ts';

// ── time.teamDay (pages/board-tech-menu.md, Server work) ──

export interface TeamDayVisit {
  appointmentId: string;
  jobId: string;
  jobNumber: string;
  address: string;
}
export interface TeamDayPerson {
  staffId: string;
  name: string;
  clockedInAt: string | null;
  clockedOutAt: string | null;
  current: null | {
    type: 'work' | 'drive' | 'non_job';
    startedAt: string;
    nonJobCode: string | null;
    nonJobName: string | null;
    paid: boolean;
    visit: TeamDayVisit | null;
  };
  /** Null, like the rest below, for a viewer who sees the now line only (customer service). */
  totals: { work: number; drive: number; nonJob: number } | null;
  runningNow: number | null;
  flagged: number | null;
  leftOpen: number | null;
  leftOpenOn: string | null;
  /** Earlier days' time to fix, oldest first (timesheet rules §10); null for a now-only viewer. */
  problems?: { kind: TimeProblem['kind']; day: string; entryId: string | null }[] | null;
}
export interface TeamDay {
  localDate: string;
  asOf: string;
  people: TeamDayPerson[];
}

/** The board's truck line for a technician (dispatch.board's technicians[].truck). */
export interface BoardTruck {
  locationId: string;
  name: string;
  belowMin: number;
  openRequests: number;
  picklist: 'ready' | 'notBuilt' | 'notLoaded' | null;
}

/** A technician's pen by their place on the board (1 to 8). */
export const penAt = (index: number): Pen => penOf(index) as Pen;

/** The truck line's numbers, or null without a truck. */
export function truckOf(t: BoardTruck | null | undefined): TechTruck | null {
  if (!t) return null;
  return {
    name: t.name,
    low: t.belowMin,
    requests: t.openRequests,
    ...(t.picklist ? { picklist: t.picklist } : {}),
  };
}

/** What they're on now, from their running entry and today's clock. */
export function techNowOf(p: TeamDayPerson, date: string, timeZone: string): TechNow {
  const at = (iso: string) => minuteOf(iso, date, timeZone);
  const c = p.current;
  if (c?.type === 'work')
    return {
      kind: 'working',
      address: c.visit?.address ?? 'a visit',
      since: at(c.startedAt),
      ...(c.visit ? { visitId: c.visit.appointmentId } : {}),
    };
  if (c?.type === 'drive') return { kind: 'driving', since: at(c.startedAt) };
  if (c?.type === 'non_job')
    // unpaid time is a break whatever its code; paid time by the company's own name for it
    return {
      kind: 'nonJob',
      words: !c.paid ? 'On a break' : (c.nonJobName ?? 'On non-job time'),
      since: at(c.startedAt),
    };
  if (p.clockedOutAt) return { kind: 'ended', at: at(p.clockedOutAt) };
  if (!p.clockedInAt) return { kind: 'notClockedIn' };
  // on the clock, between entries: they're between visits, so driving is the nearest word
  return { kind: 'driving', since: at(p.clockedInAt) };
}

/** When their last visit today that isn't over ends, in minutes, or null when none is left after now. */
export function bookedUntil(
  visits: readonly Pick<BoardVisit, 'start' | 'end' | 'status'>[],
  date: string,
  timeZone: string,
  now: number,
): number | null {
  const left = visits
    .filter((v) => v.status !== 'closed' && v.status !== 'cancelled')
    .map((v) => minuteOf(v.end, date, timeZone))
    .filter((m) => m > now);
  return left.length ? Math.max(...left) : null;
}

/**
 * A technician's day for TechMenu's card: time.teamDay's row for them (the timesheet's numbers),
 * their truck from the board, their bookings from their lane. A now-only viewer's row has no
 * totals: the card shows the now line only, so zeros stand in.
 */
export function techDayOf(
  p: TeamDayPerson,
  tech: Pick<BoardTech, 'visits'> & { truck?: BoardTruck | null },
  o: { pen: Pen | null; date: string; timeZone: string; now: number },
): TechDay {
  const leftOpenOn = p.leftOpenOn ? dayWords(p.leftOpenOn) : null;
  return {
    name: p.name,
    pen: o.pen,
    now: techNowOf(p, o.date, o.timeZone),
    clockIn: p.clockedInAt ? minuteOf(p.clockedInAt, o.date, o.timeZone) : null,
    totals: soFar(p, o),
    flagged: p.flagged ?? 0,
    runningNow: p.runningNow ?? (p.current ? 1 : 0),
    leftOpen: p.leftOpen ?? 0,
    ...(leftOpenOn ? { leftOpenOn } : {}),
    bookedUntil: bookedUntil(tech.visits, o.date, o.timeZone, o.now),
    truck: truckOf(tech.truck),
  };
}

/**
 * Paid minutes so far by type: time.team's totals count an entry once it ends, so the one running now
 * adds its minutes to its own type, when it's paid (the timesheet's own sum once it ends).
 */
export function soFar(
  p: Pick<TeamDayPerson, 'totals' | 'current'>,
  o: { date: string; timeZone: string; now: number },
): { work: number; drive: number; nonJob: number } {
  const t = { ...(p.totals ?? { work: 0, drive: 0, nonJob: 0 }) };
  const c = p.current;
  if (!p.totals || !c || !c.paid) return t;
  const running = Math.max(0, o.now - minuteOf(c.startedAt, o.date, o.timeZone));
  if (c.type === 'work') t.work += running;
  else if (c.type === 'drive') t.drive += running;
  else t.nonJob += running;
  return t;
}

/** The card for a technician time.teamDay hasn't answered for yet (loading, failed, or not listed). */
export function placeholderDay(name: string, pen: Pen | null, truck?: BoardTruck | null): TechDay {
  return {
    name,
    pen,
    now: { kind: 'notClockedIn' },
    clockIn: null,
    totals: { work: 0, drive: 0, nonJob: 0 },
    flagged: 0,
    runningNow: 0,
    leftOpen: 0,
    truck: truckOf(truck),
  };
}

/** "2026-10-23" as "Fri Oct 23": a calendar date, no time zone in it. */
export function dayWords(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][at.getUTCDay()]!;
  const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]!;
  return `${wd} ${mo} ${d}`;
}

/** Their time problems in the shape the card and the lane word (oldest first). */
export function problemsOf(p: TeamDayPerson | undefined): TimeProblem[] {
  return (p?.problems ?? []).map((x) => ({ kind: x.kind, date: x.day }));
}

/**
 * Who the menu is drawn for (techMenuAccess): by role where the person has one of the menu's, else by
 * what they hold: correcting hours reads as a bookkeeper's view, dispatching as a dispatcher's, the
 * rest the now line only.
 */
export function techMenuRole(
  roles: readonly string[] | undefined,
  permissions: readonly string[],
): TechMenuRole {
  const r = roles ?? [];
  for (const k of ['owner', 'manager', 'bookkeeper', 'dispatcher'] as const) if (r.includes(k)) return k;
  if (r.includes('csr')) return 'csr';
  if (permissions.includes('time.correct')) return 'bookkeeper';
  if (permissions.includes('jobs.dispatch')) return 'dispatcher';
  return 'csr';
}

/** Those who fix hours: Timesheet, Fix and Edit hours are theirs (the coordinator's call 6). */
export const fixesHours = (role: TechMenuRole) =>
  role === 'owner' || role === 'manager' || role === 'bookkeeper';

// ── The lanes ──

/**
 * The lane head's clock line in the narrow names column: clockLine's (board-extras-model) facts in
 * fewer words, the clock times bare ("Clocked in 7:52", "Clocked out, 2 left"); clockLine's own text
 * stays the line's title. Out on a visit today, it names the job they're out on instead ("Out · #24580
 * Fairview residence", outChip), as the classic board's Out chip did: what's happening with them now.
 * Null where clockLine has none.
 */
export function laneClockWords(
  c: { in: string | null; out: string | null } | undefined,
  line: { text: string; warn?: boolean } | null,
  o: { date: string; today: string; timeZone: string; booked?: number; out?: string | null },
): { words: string; bad?: boolean } | null {
  const today = o.date === o.today;
  // out on a visit: said on today's board only, never on another day's; a break or a clock-out first
  const out = today && o.out && !c?.out && line?.text !== 'On a break' ? o.out : null;
  if (!line) return out ? { words: out } : null;
  if (!c?.in) return out ? { words: out } : { words: line.text };
  const bare = (t: string) => clock(t, o.timeZone).time;
  // clocked in on a day gone by and never out (QA-764): flagged, on that day's board or a later one
  if (!c.out && (!today || localDateOf(c.in, o.timeZone) < o.date))
    return {
      words:
        localDateOf(c.in, o.timeZone) < o.date
          ? `In since ${dayWords(localDateOf(c.in, o.timeZone))}`
          : `In ${bare(c.in)}, no clock-out`,
      bad: true,
    };
  if (c.out && today && o.booked) return { words: `Clocked out, ${o.booked} left`, bad: true };
  if (c.out) return { words: today ? `Clocked out ${bare(c.out)}` : `Clocked in ${bare(c.in)}` };
  if (today && line.text === 'On a break') return { words: line.text };
  if (out) return { words: out };
  return { words: `Clocked in ${bare(c.in)}` };
}

/** The span the board draws: 7 a.m. to its end hour, in minutes. */
export const spanOf = (endHour: number) => ({ from: DAY_START_HOUR * 60, to: endHour * 60 });

/** A visit's drawing on a lane: the state its own technician is at (a shared visit), nobody's in No one yet. */
export function visitState(
  v: Pick<BoardVisit, 'status' | 'techs' | 'notStarted' | 'techIds' | 'pauseReason'>,
  techId: string | null,
): VisitState {
  if (techId === null && !v.techIds.length) return 'unassigned';
  const st = laneStatus(v, techId);
  if (st === 'dispatched') return v.notStarted ? 'sent' : 'on-the-way';
  if (st === 'working') return 'working';
  if (st === 'paused') return 'paused';
  // a tech's own field status says done; the visit's says closed
  if (st === 'closed' || st === 'done') return 'done';
  return 'booked';
}

/** The start alone, for a block too narrow for its window: "10", "1:30". */
export function startWord(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  const m = minute % 60;
  const h12 = ((h + 11) % 12) + 1;
  return m ? `${h12}:${String(m).padStart(2, '0')}` : String(h12);
}

/** The words a late or long visit puts on its block: "Late 25 min", "Running long 40 min". */
export function lateWords(o: Overdue | null): string | null {
  if (!o || overdueTone(o) === 'stale') return null;
  const why = lookWhy(o);
  return why.charAt(0).toUpperCase() + why.slice(1);
}

/** A visit's own times, "10–11 a.m.": what it's booked for, whatever window the customer was given. */
export const ownTimes = (v: Pick<BoardVisit, 'start' | 'end'>, tz: string): string =>
  windowText({ start: v.start, end: v.end, arrivalWindow: null }, tz);

/** The arrival window the customer was given, when it isn't the visit's own times; else null. */
export function arrivalWindowText(
  v: Pick<BoardVisit, 'start' | 'end' | 'arrivalWindow'>,
  tz: string,
): string | null {
  const w = v.arrivalWindow;
  if (!w || (Date.parse(w.start) === Date.parse(v.start) && Date.parse(w.end) === Date.parse(v.end)))
    return null;
  return windowText(v, tz);
}

/** "10:00 a.m. to 11:00 a.m." (QA-762): a visit's own start and end, as its block's label says them. */
export function spokenTimes(v: Pick<BoardVisit, 'start' | 'end'>, tz: string): string {
  const s = clock(v.start, tz);
  const e = clock(v.end, tz);
  return `${s.time} ${s.suffix} to ${e.time} ${e.suffix}`;
}

/**
 * QA-764: how a visit stands once its day has gone by. 'missed': still booked or sent, so nobody went
 * (Not attended); 'open': still on site, left open overnight, for someone to look at. Null on today or a
 * later day, and for a visit that ran its course (done, paused, cancelled).
 */
export function pastState(
  v: Pick<BoardVisit, 'status' | 'start'>,
  tz: string,
  today: string,
): 'missed' | 'open' | null {
  if (localDateOf(v.start, tz) >= today) return null;
  if (v.status === 'scheduled' || v.status === 'dispatched') return 'missed';
  if (v.status === 'working') return 'open';
  return null;
}

/** The words a visit left on site overnight carries, in red: "Still on site since Mon Oct 26". */
export const leftOpenWords = (v: Pick<BoardVisit, 'start'>, tz: string) =>
  `Still on site since ${dayWords(localDateOf(v.start, tz))}`;

/** A visit's state in words on a lane: Not attended once its day went by unvisited (QA-764). */
export const NOT_ATTENDED = 'Not attended';

/**
 * A technician's name in a phone's narrow names column (QA-765): the first name and the last one's
 * initial ("Alex M."); a one-word name as it is.
 */
export function shortName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return name.trim();
  return `${words[0]} ${words.at(-1)!.charAt(0).toUpperCase()}.`;
}

export interface VisitOn {
  tz: string;
  date: string;
  /** The company's today: a visit on a day gone by is marked (QA-764); absent, nothing is. */
  today?: string;
  /** The visit's lane: its technician, or null for No one yet. */
  techId: string | null;
  overdue: Overdue | null;
  /** Today's minute, for a visit running long's over-run. */
  now: number | null;
  /** Its technicians' names, for the hover card. */
  techName: (id: string) => string;
  isNow?: boolean;
  isNext?: boolean;
  statusLabels: Record<string, string>;
  /** A visit's panel is open: no block's hover card, so none is drawn over the panel (QA-841). */
  panelOpen?: boolean;
}

/**
 * The label every visit block has said since BL-062 (qa/web's locators read it), its times the visit's
 * own start and end (QA-762), and the arrival window last where it differs: "10:00 a.m. to 11:00 a.m.,
 * job 24611, Hollis residence, On site, now, late, needs a licence, tags Warranty, arrival window 10–12
 * p.m.". A visit whose day went by unvisited says Not attended; one left on site, that (QA-764).
 */
/** The visit's state in the lane's words: "Not attended" for one its day went by (QA-764). */
function stateWords(v: BoardVisit, on: VisitOn): string {
  const st = laneStatus(v, on.techId);
  const shown = st === v.status ? v : { ...v, status: st, notStarted: st === 'dispatched' && v.notStarted };
  return on.today && pastState(v, on.tz, on.today) === 'missed'
    ? NOT_ATTENDED
    : visitStatusLabel(shown, on.statusLabels);
}

export function visitLabel(v: BoardVisit, on: VisitOn): string {
  const past = on.today ? pastState(v, on.tz, on.today) : null;
  const state = stateWords(v, on);
  const badge = on.overdue ? overdueWords(on.overdue, on.tz).badge : null;
  const needs = (v.licenceGaps ?? []).length > 0;
  const said = techMessagesWords(v.newFromTechs);
  const window = arrivalWindowText(v, on.tz);
  return `${spokenTimes(v, on.tz)}, job ${v.jobNumber}, ${v.customerName}, ${state}${past === 'open' ? `, ${leftOpenWords(v, on.tz).toLowerCase()}` : ''}${on.isNow ? ', now' : ''}${on.isNext ? ', next' : ''}${badge ? `, ${badge.toLowerCase()}` : ''}${needs ? ', needs a licence' : ''}${said ? `, ${said}` : ''}${tagsLabel(v.tags)}${window ? `, arrival window ${window}` : ''}`;
}

/** The summary's own words (its scope, as the job page reads it), or the summary as text from a board before it. */
const workOf = (v: Pick<BoardVisit, 'summary' | 'scope'>, lines?: number): string =>
  v.scope !== undefined ? (lines ? scopeLead(v.scope, lines) : v.scope) : plainText(v.summary);

/** What the hover card adds: only what the block doesn't say in full (BL-158 9). */
export function visitHover(v: BoardVisit, on: VisitOn): KeyValueItem[] {
  const out: KeyValueItem[] = [];
  // it leads with the work, its first two lines
  const work = workOf(v, 2);
  if (work) out.push({ label: 'Summary', value: work });
  // the customer whole: the block cuts a long name short with "…"
  out.push({ label: 'Customer', value: v.customerName }, { label: 'Address', value: v.address });
  if (on.overdue && overdueTone(on.overdue) === 'stale')
    out.push({ label: 'As of', value: overdueWords(on.overdue, on.tz).line });
  // the block says its own times (QA-762); the window the customer was given, where it differs
  const window = arrivalWindowText(v, on.tz);
  if (window) out.push({ label: 'Arrival window', value: window });
  const others = v.techIds.filter((id) => id !== on.techId);
  if (others.length)
    out.push({ label: on.techId ? 'With' : 'Technicians', value: others.map(on.techName).join(', ') });
  // handed in with the work done: the block's state says so (QA-830)
  if (v.status === 'paused' && v.pauseReason && !workDone(v))
    out.push({ label: 'Paused', value: pauseWords(v.pauseReason) });
  if (v.tags?.length) out.push({ label: 'Tags', value: v.tags.map((t) => t.label).join(', ') });
  if (v.siteContactName) out.push({ label: 'Site contact', value: v.siteContactName });
  const gaps = gapsText(v.licenceGaps ?? []);
  if (gaps) out.push({ label: 'Licence', value: gaps, bad: true });
  const said = techMessagesWords(v.newFromTechs);
  if (said) out.push({ label: 'Messages', value: said });
  // a visit under an hour can draw as its name alone, or a start figure (QA-763): its times and
  // state whole here
  if (Date.parse(v.end) - Date.parse(v.start) < 60 * 60_000)
    out.push({ label: 'Visit', value: `${ownTimes(v, on.tz)}, ${stateWords(v, on)}` });
  return out;
}

/** A BoardVisit as its lane draws it. */
export function laneVisitOf(v: BoardVisit, on: VisitOn): LaneVisit {
  // started: drawn from when work began, not its booked slot (QA-844)
  const span = shownSpan(v);
  const start = minuteOf(span.start, on.date, on.tz);
  const end = Math.max(minuteOf(span.end, on.date, on.tz), start + 5);
  const o = on.overdue;
  const past = on.today ? pastState(v, on.tz, on.today) : null;
  return {
    id: v.appointmentId,
    start,
    end,
    // a day gone by: a visit nobody went to is Not attended, drawn, not coloured (QA-764)
    state: past === 'missed' ? 'missed' : visitState(v, on.techId),
    // its own times, the window in its label and hover card where it differs (QA-762)
    time: ownTimes(v, on.tz),
    timeShort: startWord(start),
    customer: v.customerName,
    job: `#${v.jobNumber}`,
    // left on site overnight: a problem, for someone to look at (QA-764)
    late: past === 'open' ? 'Still on site' : lateWords(o),
    // the visit they're on now, or the one queued to go out next for them (BL-070)
    ...(on.isNow ? { tag: 'now' as const } : on.isNext ? { tag: 'next' as const } : {}),
    // BL-027: nobody on it holds a licence the work needs
    ...((v.licenceGaps ?? []).length ? { licence: true } : {}),
    label: visitLabel(v, on),
    ...(o && o.kind === 'long' && overdueTone(o) !== 'stale' && on.now !== null && on.now > end
      ? { overUntil: on.now }
      : {}),
    ...(on.panelOpen ? {} : { hover: visitHover(v, on) }),
  };
}

/** Their time away on the lane: hours they don't work, time off, holidays, non-job time (BL-014). */
export function laneOffOf(
  tech: Pick<BoardTech, 'days'>,
  date: string,
  tz: string,
  endHour: number,
  nowIso: string,
): LaneOff[] {
  const span = (endHour - DAY_START_HOUR) * 60;
  const at = (pct: number) => Math.round(DAY_START_HOUR * 60 + (pct / 100) * span);
  return laneBands(
    tech.days?.find((d) => d.date === date),
    tz,
    endHour,
    nowIso,
  ).map((b) => ({ start: at(b.left), end: at(b.left + b.width), label: b.label ?? 'Off' }));
}

/** The line under a technician's name that says their day: how much is booked, or what's late, in red. */
export function laneDayLine(
  visits: readonly BoardVisit[],
  overdueOf: (v: BoardVisit) => Overdue | null,
  tz: string,
): LaneLine {
  const late = visits.map(overdueOf).find((o) => o && overdueTone(o) !== 'stale');
  if (late) {
    const why = lookWhy(late, tz);
    return { words: why.charAt(0).toUpperCase() + why.slice(1), bad: true };
  }
  return { words: laneSummary(visits) };
}

// ── The band over the board, the status bar and the table under it ──

/** The band's figures: red only for what needs someone (late, nobody on it). */
export function bandFigures(f: {
  visits: number;
  working: number;
  done: number;
  late: number;
  waiting: number;
  /** A day gone by: its visits nobody went to (QA-764), said in place of late, never in red. */
  missed?: number;
}): BandFigure[] {
  return [
    { words: `${f.visits} ${f.visits === 1 ? 'visit' : 'visits'}` },
    { words: `${f.working} working` },
    { words: `${f.done} done` },
    f.missed !== undefined
      ? { words: `${f.missed} not attended` }
      : { words: `${f.late} late`, bad: f.late > 0 },
    { words: `${f.waiting} no one yet`, bad: f.waiting > 0 },
  ];
}

/**
 * The status bar's day tabs, in the order a day goes; then the jobs not on the day's lanes. A visit
 * state's tab says the shared word for it (@flowology/ui-web/words), as the filter and the blocks do.
 */
export const DAY_TABS = [
  ['all', 'All visits'],
  ['look', 'Needs a look'],
  ['nobody', 'No one yet'],
  ['scheduled', VISIT_WORDS.scheduled],
  ['dispatched', VISIT_WORDS.dispatched],
  ['working', VISIT_WORDS.working],
  ['paused', VISIT_WORDS.paused],
  ['closed', VISIT_WORDS.closed],
] as const;
export type DayTab = (typeof DAY_TABS)[number][0];
export type BoardTab = DayTab | ListTab;

export const isListTab = (t: string): t is ListTab => LIST_TABS.some(([k]) => k === t);

/**
 * The status bar: the day's tabs with their counts, then (after a rule) those of any day: Paused and
 * the lists (BL-127). Each group says its scope (statusScopes, QA-768): Paused counts every paused
 * visit, whatever its day, so it sits with the lists, never among the day's. Needs a look is for
 * those who can dispatch only, as the classic board's tray was (a CSR's board has none).
 */
export function statusGroups(c: {
  day: readonly BoardVisit[];
  looks: number;
  waiting: number;
  paused: number;
  lists: Partial<Record<ListTab, number>> | null;
  canLook: boolean;
}): StatusTab[][] {
  const by = (s: string) => c.day.filter((v) => shownStatus(v) === s).length;
  const count: Record<DayTab, number> = {
    all: c.day.length,
    look: c.looks,
    nobody: c.waiting,
    scheduled: by('scheduled'),
    dispatched: by('dispatched'),
    working: by('working'),
    paused: c.paused,
    closed: by('closed'),
  };
  const tab = ([id, label]: (typeof DAY_TABS)[number]): StatusTab => ({
    id,
    label,
    count: count[id],
    urgent: id === 'look' || id === 'nobody',
  });
  return [
    DAY_TABS.filter(([id]) => id !== 'paused' && (id !== 'look' || c.canLook)).map(tab),
    [
      ...DAY_TABS.filter(([id]) => id === 'paused').map(tab),
      ...LIST_TABS.map(([id, label]) => ({
        id,
        label: label.replace(' (WIP)', ''),
        count: c.lists?.[id] ?? 0,
        // a missed visit needs someone to rebook or close it (QA-850)
        urgent: id === 'missed',
      })),
    ],
  ];
}

/**
 * QA-768: what each of statusGroups' groups counts, said before its tabs: the board's day ("Today’s
 * visits", "Friday’s visits"), then "Any day". Labelled rather than one scope for all: Future, On hold
 * and Unscheduled have no day to be counted on.
 */
export const statusScopes = (date: string, today: string): [string, string] => [
  boardTitle(date, today).replace(/board$/, 'visits'),
  'Any day',
];

/** The visits a day tab lists. */
export function dayTabVisits(
  tab: DayTab,
  c: {
    day: readonly BoardVisit[];
    looks: readonly BoardVisit[];
    unassigned: readonly BoardVisit[];
    paused: readonly BoardVisit[];
  },
): BoardVisit[] {
  if (tab === 'all') return [...c.day];
  if (tab === 'look') return [...c.looks];
  if (tab === 'nobody') return [...c.unassigned];
  if (tab === 'paused') return [...c.paused];
  return c.day.filter((v) => shownStatus(v) === tab);
}

export const DAY_COLUMNS: JobColumn[] = ['window', 'customer', 'job', 'tech', 'type', 'area', 'tags'];
export const LOOK_COLUMNS: JobColumn[] = ['why', 'window', 'customer', 'job', 'tech', 'area'];
export const LIST_COLUMNS: JobColumn[] = ['window', 'customer', 'job', 'why'];
/** Paused: why and since when first, as the classic board's tray said it. */
export const PAUSED_COLUMNS: JobColumn[] = ['paused', 'window', 'customer', 'job', 'tech', 'area'];

/** The town an address ends in ("1184 Larch Way, Langford" is Langford). */
export const areaOf = (address: string) =>
  address
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .at(-1) ?? '';

/** A day's visit as a table row; its id is the visit's, so pressing it opens the visit. */
export function visitRow(
  v: BoardVisit,
  o: {
    tz: string;
    date: string;
    techName: (id: string) => string;
    pen: (id: string) => Pen | null;
    why?: string | null;
    paused?: string | null;
  },
): JobRow {
  const lead = v.leadTechId ?? v.techIds[0] ?? null;
  const crew = v.techIds.map(o.techName).join(', ');
  return {
    id: v.appointmentId,
    job: `#${v.jobNumber}`,
    received: '',
    receivedAt: 0,
    priority: 'Normal',
    type: v.jobType?.name ?? plainText(v.summary),
    window: windowText(v, o.tz),
    booked: v.arrivalWindow ? windowText({ start: v.start, end: v.end, arrivalWindow: null }, o.tz) : null,
    windowAt: minuteOf(v.arrivalWindow?.start ?? v.start, o.date, o.tz),
    tech: lead ? { name: crew, pen: o.pen(lead) } : null,
    customer: v.customerName,
    area: areaOf(v.address),
    address: v.address,
    tags: (v.tags ?? []).map((t) => t.label),
    total: 0,
    why: o.why ?? null,
    ...(o.paused ? { paused: o.paused } : {}),
  };
}

/** A paused visit's why and since when, for its row: "Needs parts, since 9:40 a.m.". */
export function pausedRowWords(
  v: Pick<BoardVisit, 'pauseReason' | 'pausedAt' | 'start'>,
  tz: string,
  today: string,
) {
  const why = v.pauseReason ? pauseWords(v.pauseReason) : 'No reason given';
  const since = pausedSince(v, tz, today);
  return `${why}, ${since.charAt(0).toLowerCase()}${since.slice(1)}`;
}

/**
 * The Paused tab's tables: one for each reason, in the tech app's order, the reason and how many as its
 * title ("Needs parts (2)"), as the classic board's tray had them; each row says why and since when.
 */
export function pausedTables(
  paused: readonly BoardVisit[],
  o: {
    tz: string;
    date: string;
    today: string;
    techName: (id: string) => string;
    pen: (id: string) => Pen | null;
  },
): { key: string; title: string; rows: JobRow[] }[] {
  return pausedGroups(paused).map((g) => ({
    key: g.reason ?? 'none',
    title: `${g.label} (${g.visits.length})`,
    rows: g.visits.map((v) => visitRow(v, { ...o, paused: pausedRowWords(v, o.tz, o.today) })),
  }));
}

/** A list tab's row (On hold, Future…): when, who for, why; its id is the job's (a row opens the job). */
export function listJobRow(r: {
  key: string;
  jobId: string;
  jobNumber: string;
  customer: string;
  when: string;
  why: string;
}): JobRow {
  return {
    id: r.key,
    job: `#${r.jobNumber}`,
    received: '',
    receivedAt: 0,
    priority: 'Normal',
    type: '',
    window: r.when,
    windowAt: null,
    tech: null,
    customer: r.customer,
    area: '',
    address: '',
    tags: [],
    total: 0,
    why: plainText(r.why) || null,
  };
}

/**
 * A technician's route on the map strip: their pen, name and stops. A stop whose address has no
 * location isn't on the map, so the route says so rather than count a stop the map can't show (QA-852).
 */
export function routeOf(t: Pick<BoardTech, 'name' | 'visits'>, pen: Pen | null) {
  const stops = t.visits.length;
  if (!stops) return { name: t.name, pen, line: 'Nothing booked' };
  const count = `${stops} ${stops === 1 ? 'stop' : 'stops'}`;
  const off = t.visits.filter((v) => !v.geo).length;
  const why = (n: number) =>
    `not on the map (no location for ${n === 1 ? 'its address' : 'their addresses'})`;
  const line = !off ? count : stops === 1 ? `${count}, ${why(1)}` : `${count}, ${off} ${why(off)}`;
  return { name: t.name, pen, line };
}

// ── The side panel ──

/**
 * The open visit's fields in the side panel: what its first lines don't say. Its state; on a shared
 * visit where they aren't all at the same point, where each is; then where, what, and who to ask.
 */
export function panelFields(
  v: BoardVisit,
  o: {
    techName: (id: string) => string;
    statusLabels: Record<string, string>;
    pausedSince?: string | null;
    /** The company's today and zone: a visit whose day went by unvisited is Not attended (QA-764). */
    today?: string;
    tz?: string;
  },
): KeyValueItem[] {
  const past = o.today && o.tz ? pastState(v, o.tz, o.today) : null;
  const out: KeyValueItem[] = [
    { label: 'State', value: past === 'missed' ? NOT_ATTENDED : visitStatusLabel(v, o.statusLabels) },
  ];
  // the heading says its own times (QA-762); the window the customer was given, where it differs
  const window = o.tz ? arrivalWindowText(v, o.tz) : null;
  if (window) out.push({ label: 'Arrival window', value: window });
  const apart = (v.techs?.length ?? 0) > 1 && new Set(v.techs!.map((t) => laneStatus(v, t.id))).size > 1;
  if (apart)
    for (const t of v.techs!)
      out.push({
        label: o.techName(t.id),
        value: o.statusLabels[laneStatus(v, t.id)] ?? laneStatus(v, t.id),
      });
  out.push({ label: 'Address', value: v.address });
  const work = workOf(v);
  if (work) out.push({ label: 'Summary', value: work });
  if (v.dispatchNotes?.length) out.push({ label: 'Dispatch notes', value: v.dispatchNotes.join('\n') });
  if (v.buzzer) out.push({ label: 'Buzzer', value: v.buzzer });
  if (v.priority) out.push({ label: 'Priority', value: v.priority });
  // handed in with the work done: State says so (QA-830); when, here
  if (workDone(v)) {
    if (o.pausedSince) out.push({ label: 'Handed in', value: o.pausedSince });
  } else if (v.status === 'paused' && v.pauseReason)
    out.push({
      label: 'Paused',
      value: o.pausedSince
        ? `${pauseWords(v.pauseReason)}, ${o.pausedSince.toLowerCase()}`
        : pauseWords(v.pauseReason),
    });
  // what the technician said as they paused it, in their words (QA-833)
  if (v.status === 'paused' && v.pauseNote)
    out.push({
      label: v.pauseNote.staffId ? `${o.techName(v.pauseNote.staffId)}’s note` : 'Their note',
      value: v.pauseNote.body,
    });
  if (v.siteContactName) out.push({ label: 'Site contact', value: v.siteContactName });
  if (v.tags?.length) out.push({ label: 'Tags', value: v.tags.map((t) => t.label).join(', ') });
  const said = techMessagesWords(v.newFromTechs);
  if (said) out.push({ label: 'Messages', value: `${said}. Open the job to read them.` });
  return out;
}

/** The panel's red line: what's wrong with it now (late, a licence nobody holds), or nothing. */
export function panelNote(v: BoardVisit, o: Overdue | null, tz: string, today?: string): string | null {
  const gaps = gapsText(v.licenceGaps ?? []);
  const late = o && overdueTone(o) !== 'stale' ? overdueWords(o, tz).line : null;
  // left on site overnight (QA-764): nobody finished it on its day
  const open =
    today && pastState(v, tz, today) === 'open' ? `${leftOpenWords(v, tz)}: it was never finished` : null;
  return [open, late, gaps].filter(Boolean).join('. ') || null;
}

/**
 * The open visit's heading (QA-762): its own times and who for, with its day when it isn't the board's
 * ("Mon, Oct 26, 10–11 a.m., Okafor family").
 */
export function panelTitle(
  v: Pick<BoardVisit, 'start' | 'end' | 'customerName'>,
  o: { tz: string; date: string; dayText: (day: string) => string },
): string {
  const day = localDateOf(v.start, o.tz);
  return `${day !== o.date ? `${o.dayText(day)}, ` : ''}${ownTimes(v, o.tz)}, ${v.customerName}`;
}

/** A division's heading: how many technicians and visits, and what's running late, in red. */
export function divisionLines(
  techs: readonly Pick<BoardTech, 'visits'>[],
  overdueOf: (v: BoardVisit) => Overdue | null,
): { summary: string; bad: string | null } {
  const n = techs.length;
  const visits = techs.flatMap((t) => t.visits);
  const late = new Set(
    visits
      .filter((v) => {
        const x = overdueOf(v);
        return x && overdueTone(x) !== 'stale';
      })
      .map((v) => v.appointmentId),
  ).size;
  return {
    summary: `${n} ${n === 1 ? 'technician' : 'technicians'}, ${visits.length} ${visits.length === 1 ? 'visit' : 'visits'}`,
    bad: late ? `${late} running late` : null,
  };
}

/** A phone's width: the library's touch breakpoint (StatusBar, SidePanel), where the panel covers the lanes. */
export const PHONE_WIDTH = '(max-width: 900px)';

/**
 * Whether the open visit's panel shows (QA-878): on a phone, not while the visit is picked up to be given,
 * since it would cover the lanes it's given to. It's still the open one; it shows again once given or put down.
 */
export const panelShown = (o: { picked: boolean; phone: boolean }): boolean => !(o.picked && o.phone);
